import { BASE_URL } from '../endpoints/apiEndpoints'
import { getAccessToken } from '../utils/session'

export const handleSuccessResponse = async response => {
  const contentType = response.headers.get('content-type')
  if (contentType?.includes('application/json')) {
    const data = await response.json()
    return { success: true, data }
  } else {
    const blob = await response.blob()
    return { success: true, data: blob }
  }
}

export const handleErrorResponse = error => {
  return {
    success: false,
    error: error?.message  || 'Unknown Error',
  }
}

export const handleRequestFailure = async response => {
  let errorMessage = ''
  try {
    const errorData = await response.json()
    errorMessage =
      errorData?.data ||
      errorData?.errors[0]?.message ||
      `Request failed with status: ${response?.status}`
  } catch {
    errorMessage = `Request failed with status: ${response?.status}`
  }
  throw new Error(errorMessage)
}

export const buildRequestHeader = (
  method = 'GET',
  payload = null,
  extraHeaders = {}
) => {
  const token = getAccessToken()
  const headers = {
    'Content-Type': 'application/json',
    Authorization: token,
    ...extraHeaders,
  }
  return {
    method,
    headers,
    body: payload ? JSON.stringify(payload) : undefined,
  }
}

export const urlBuilder = (endpoint, pathParams = {}, searchParams = {}) => {
  const baseUrl = BASE_URL
  let path = `${baseUrl}/${endpoint}`.replace(/\/+$/, '')
  Object.values(pathParams).forEach(value => {
    if (value !== undefined && value !== null) {
      path += `/${encodeURIComponent(value)}`
    }
  })
  const url = new URL(path)
  Object.entries(searchParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      url.searchParams.append(key, value)
    }
  })
  return url.toString()
}

// export const saveBlobAsFile = (blob, fileName) => {
//   if (!(blob instanceof Blob)) throw new Error('Invalid file data');
//   const url = window.URL.createObjectURL(blob);
//   const isSafeBlob = /^blob:https?:\/\/[a-zA-Z0-9\-._~:/?#\[\]@!$&'()*+,;=%]+$/.test(url);
//   if (!isSafeBlob) throw new Error('Unsafe blob URL');

//   const a = document.createElement('a');
//   a.setAttribute('href', url);
//   a.setAttribute('download', fileName || 'download');
//   a.setAttribute('rel', 'noopener noreferrer');
//   document.body.appendChild(a);
//   a.click();
//   document.body.removeChild(a);
//   window.URL.revokeObjectURL(url);
// };

// utils/apiUtils.js
export const saveBlobAsFile = (blob, fileName) => {
  if (!(blob instanceof Blob)) throw new Error("Invalid file data");

  const url = window.URL.createObjectURL(blob);

  // ✅ Verify that the generated blob URL is safe
  const isSafeBlob = /^blob:https?:\/\/[a-zA-Z0-9\-._~:/?#\[\]@!$&'()*+,;=%]+$/.test(url);
  if (!isSafeBlob) throw new Error("Unsafe blob URL");

  // ✅ Sanitize file name to prevent XSS or attribute injection
  const safeFileName = sanitizeFileName(fileName || "download");

  const a = document.createElement("a");
  a.setAttribute("href", url);
  a.setAttribute("download", safeFileName);
  a.setAttribute("rel", "noopener noreferrer");
  a.style.display = "none"; // also helps prevent visual clickjacking

  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);

  window.URL.revokeObjectURL(url);
};

// ✅ Safe filename sanitizer
export function sanitizeFileName(name = "") {
  if (typeof name !== "string") return "download";
  return name
    .replace(/[<>:"/\\|?*\x00-\x1F]/g, "") // remove illegal filename chars
    .replace(/[&<>'"]/g, "") // remove possible XSS characters
    .replace(/\s+/g, "_") // normalize spaces
    .substring(0, 255) // ensure safe length
    .trim();
}


// utils/safeBlobUtils.js (or a new file safeDownloadUtils.js)
export const safeDownloadFromUrl = (fileUrl, fileName) => {
  try {
    // Validate and sanitize the incoming URL
    const parsed = new URL(fileUrl);
    if (!['https:', 'http:'].includes(parsed.protocol)) {
      throw new Error('Unsafe URL protocol');
    }

    // Strict URL pattern check to prevent script injection or malformed data URLs
    const isSafeUrl = /^[a-zA-Z0-9\-._~:/?#\[\]@!$&'()*+,;=%]+$/.test(fileUrl);
    if (!isSafeUrl) throw new Error('Unsafe file URL detected');

    // Create and trigger a safe anchor click
    const a = document.createElement('a');
    a.setAttribute('href', parsed.href);
    a.setAttribute('download', fileName || 'download');
    a.setAttribute('rel', 'noopener noreferrer');
    a.setAttribute('target', '_blank');
    a.style.display = 'none';

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

  } catch (err) {
    throw err;
  }
};
